import {useEffect, useRef, useState} from 'react';
import {InformesApi, errorOf, isOk} from '../../../api/informesApi';
import {dataPanels, serializeLayout} from './informeModel';
import {canonicalSettings} from './canonical';

const DEBOUNCE_MS = 350;

/**
 * Ejecuta el render de lo que hay en pantalla (`POST client-reports/render/`) cada vez que
 * cambian los Ajustes o la plantilla, con debounce y guard de respuestas viejas: si el usuario
 * cambia el período dos veces seguidas, la respuesta de la primera no pisa a la segunda.
 *
 * El render es del BORRADOR: no exige informe guardado. Devuelve el contrato del motor
 * (`{panels:[{id,type,state,data}], period, compare_period, warnings}`).
 */
/** Lo que decide las queries: cada panel sin título ni ancho, ordenados por id (el orden no cuenta). */
export function planSignature(layout) {
    const panels = (layout.panels || [])
        .map(({title, col_span, ...rest}) => rest)
        .sort((a, b) => String(a.id).localeCompare(String(b.id)));
    return {metrics: layout.metrics, dimensions: layout.dimensions, panels};
}

/**
 * `refreshKey` fuerza un render nuevo sin que cambie nada de lo de arriba: lo usa la pantalla
 * después de elegir eventos de conversión, que cambian el resultado sin tocar ni ajustes ni plantilla.
 */
export function useInformeRender({agencyClientId, template, settings, enabled = true, refreshKey = 0}) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const requestId = useRef(0);

    // La firma es la del PLAN del backend: tipos, métricas, dimensiones, granos. Ni el título ni
    // el orden de los paneles cambian una query, así que renombrar o arrastrar no re-ejecuta
    // (C-informes-44).
    // Los bloques de texto quedan afuera del pedido: el motor no los consulta, y con su contenido
    // en la firma cada tecla volvía a calcular todas las consultas del informe.
    const fullLayout = serializeLayout(template);
    const layout = {...fullLayout, panels: dataPanels(fullLayout.panels)};
    const signature = JSON.stringify({
        client: agencyClientId,
        settings: canonicalSettings(settings),
        plan: planSignature(layout),
        refreshKey,
    });

    useEffect(() => {
        if (!enabled || !agencyClientId || !layout.panels.length) {
            // Sin nada que renderizar tampoco hay nada en vuelo: si se borró el último panel con un
            // render pendiente, el folio quedaba al 60 % para siempre (C-informes-43).
            requestId.current += 1;
            setResult(null);
            setLoading(false);
            setError(null);
            return undefined;
        }
        const id = requestId.current + 1;
        requestId.current = id;
        setLoading(true);
        setError(null);
        const timer = setTimeout(() => {
            InformesApi.renderDraft(agencyClientId, layout, settings)
                .then((response) => {
                    if (requestId.current !== id) return;
                    if (!isOk(response)) {
                        setError(errorOf(response, null));
                        setResult(null);
                    } else {
                        setResult(response.data);
                    }
                    setLoading(false);
                })
                .catch(() => {
                    if (requestId.current !== id) return;
                    setError({code: 'NETWORK_ERROR', message: null});
                    // Sin esto el resultado ANTERIOR sobrevive al error y la pantalla queda
                    // mostrando los números del render viejo debajo de una franja roja, como si
                    // fueran los del período que se acaba de pedir.
                    setResult(null);
                    setLoading(false);
                });
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, enabled]);

    return {result, loading, error};
}
